import React, { useState, useEffect } from 'react';
import { FiMapPin, FiUsers, FiCalendar, FiArrowRight } from 'react-icons/fi';
import { BiTargetLock } from 'react-icons/bi';
import { TbTrophy } from 'react-icons/tb';
import { MdWavingHand } from 'react-icons/md';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { matchAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useAuthContext();
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadMatches = async () => {
      try {
        setLoading(true);
        const response = await matchAPI.getAllMatches({ status: 'upcoming' });
        if (response.success) {
          setAllMatches(response.matches);
        }
      } catch (err) {
        setError(err.message);
        setAllMatches([]);
      } finally {
        setLoading(false);
      }
    };

    loadMatches();
  }, []);

  const getId = (val) => {
    if (!val) return null;
    if (typeof val === 'string') return val;
    return val._id?.toString() || val.toString();
  };

  const userId = getId(user?._id || user?.id);

  // Calculate stats dynamically
  let createdCount = 0;
  let joinedCount = 0;

  allMatches.forEach(match => {
    const isCreator = getId(match.createdBy) === userId;
    const isParticipant = match.participants.some(p => getId(p) === userId);
    
    if (isCreator) createdCount++;
    if (isParticipant && !isCreator) joinedCount++; 
  });

  const stats = [
    { icon: <FiCalendar className="w-8 h-8" />, number: allMatches.length, label: 'Matches Available' },
    { icon: <BiTargetLock className="w-8 h-8" />, number: createdCount, label: 'Matches Created' },
    { icon: <TbTrophy className="w-8 h-8" />, number: joinedCount + createdCount, label: 'Upcoming Matches' },
  ];

  // Display only 6 matches in the grid
  const displayMatches = allMatches.slice(0, 6);

  return (
    <div className="bg-dark min-h-screen">
      <Navbar hideAuthButtons={true} />

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-dark via-darkGray to-dark py-12 px-4 sm:px-6 lg:px-8 border-b border-gray-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-red-500/10 rounded-full blur-3xl -z-10"></div>
        <div className="max-w-7xl mx-auto">
          <div className="space-y-2">
            <p className="text-red-500 text-sm font-semibold uppercase tracking-wider">Welcome back</p>
            <h1 className="text-5xl md:text-6xl font-bold text-white flex items-center gap-4">
              Hey, {user?.fullName?.split(' ')[0] || 'Friend'}!
              <MdWavingHand className="w-12 h-12 text-red-500 animate-pulse" />
            </h1>
            <p className="text-gray-400 text-lg">
              {allMatches.length} awesome matches waiting for you to join
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="bg-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="card-base card-hover group relative p-8 overflow-hidden"
              >
                {/* Background gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10 space-y-3">
                  <div className="text-red-500">
                    {stat.icon}
                  </div>
                  <div className="text-5xl font-bold text-gradient">
                    {stat.number}
                  </div>
                  <div className="text-gray-400 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Recommended Section */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-10">
              <div>
                <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2">Curated for you</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white">Recommended Matches</h2>
              </div>
              <Link to="/discover" className="text-red-500 hover:text-red-400 text-sm font-bold flex items-center gap-2 hover:gap-3 transition-all">
                View All <FiArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Matches Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading ? (
                <div className="col-span-full flex justify-center py-12">
                  <div className="animate-pulse space-y-4 w-full max-w-md">
                    <div className="h-48 bg-gray-800 rounded-lg"></div>
                    <div className="h-4 bg-gray-800 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-800 rounded w-1/2"></div>
                  </div>
                </div>
              ) : error ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-red-500 text-lg mb-4">Error loading matches</p>
                  <p className="text-gray-400">{error}</p>
                </div>
              ) : displayMatches.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-400 text-lg mb-4">No matches available yet</p>
                  <Link to="/create-match" className="btn-primary inline-block">
                    Create a Match
                  </Link>
                </div>
              ) : (
                displayMatches.map((match) => (
                  <Link key={match._id} to={`/match/${match._id}`} className="card-base card-hover overflow-hidden group">
                    {/* Match Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={match.matchImage || "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=300&fit=crop"}
                        alt={match.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      
                      {/* Spots Left Badge */}
                      <div className="absolute top-4 right-4 badge-primary bg-red-600/80 border-red-500">
                        {match.spotsLeft || match.maxPlayers - (match.participants?.length || 0)} spots left
                      </div>

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent"></div>
                    </div>

                    {/* Match Info */}
                    <div className="p-6 space-y-4">
                      {/* Title */}
                      <h3 className="text-white font-bold text-lg group-hover:text-red-500 transition-colors line-clamp-2">
                        {match.title}
                      </h3>

                      {/* Details */}
                      <div className="space-y-2 text-sm text-gray-400">
                        {/* Location */}
                        <div className="flex items-center gap-2">
                          <FiMapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{match.location}</span>
                        </div>

                        {/* Date */}
                        <div className="flex items-center gap-2">
                          <FiCalendar className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{match.date && new Date(match.date).toLocaleDateString()}</span>
                        </div>

                        {/* Players */}
                        <div className="flex items-center gap-2">
                          <FiUsers className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{match.participants?.length || 0}/{match.maxPlayers} players</span>
                        </div>
                      </div>

                      {/* View Button */}
                      <button className="w-full mt-4 bg-gradient-to-r from-red-600 to-red-500 hover:shadow-glow-red text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 group-hover:scale-105 flex items-center justify-center gap-2">
                        Join Match <FiArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="h-1 bg-gradient-to-r from-red-500 via-red-500/50 to-transparent"></div>
    </div>
  );
};

export default Dashboard;
